import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  OverlayDrawer,
  OverlayDrawerProps,
  Spinner,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useId,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import './GuideComponents.css';

/**
 * Drawer (panel lateral) de la guía de diseño. Spec: `.docs/styleGuideReferemce/design-tokens.md`,
 * sección "Drawer (panel lateral)". Envuelve el OverlayDrawer de Fluent v9 con:
 * encabezado (contexto opcional + título + acciones + cerrar), cuerpo con scroll propio y
 * pie fijo con las acciones en el orden de la guía.
 */

export type GuideDrawerSize = 'small' | 'medium' | 'large' | 'wide' | 'full';

/**
 * `wide` (vistas previas): min(1100 px, 75 % de la ventana) y 100 % en pantallas de menos de 768 px.
 * El ancho lo pone GuideComponents.css (`.ichtys-drawer[data-size="wide"]`) porque depende de un media query.
 */
export const GUIDE_DRAWER_WIDE_WIDTH = 'min(1100px, 75vw)';
export const GUIDE_DRAWER_NARROW_BREAKPOINT_PX = 768;

/** Anchos de la guía (px). `full` ocupa todo el ancho de la ventana; `wide` ver GUIDE_DRAWER_WIDE_WIDTH. */
export const GUIDE_DRAWER_WIDTHS: Record<Exclude<GuideDrawerSize, 'full' | 'wide'>, number> = {
  small: 480,
  medium: 640,
  large: 900,
};

export interface GuideDrawerAction {
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Muestra un spinner en lugar del ícono y deshabilita el botón. */
  loading?: boolean;
  icon?: React.ReactElement;
  /** `submit` + `form` permite enviar un `<form id=...>` del cuerpo desde el pie. */
  type?: 'button' | 'submit';
  form?: string;
  ariaLabel?: string;
  testId?: string;
}

export interface GuideDrawerProps {
  open: boolean;
  /** Se llama al cerrar con Esc, con la X, con el backdrop o con el "Cancelar" por defecto. */
  onDismiss: () => void;
  /** Opcional: recibe todos los cambios de apertura de Fluent (además de `onDismiss` al cerrar). */
  onOpenChange?: OverlayDrawerProps['onOpenChange'];
  /** small 480 · medium 640 (formularios, por defecto) · large 900 (detalle / listas) · wide min(1100, 75vw) (vistas previas) · full. */
  size?: GuideDrawerSize;
  /** Línea de contexto arriba del título (13 Regular, texto secundario). Ej.: "Sponsor 12 · Protocolo 31". */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** Línea secundaria debajo del título (13 Regular, texto secundario). */
  subtitle?: React.ReactNode;
  /** Acciones del encabezado (botones sutiles), a la izquierda de la X. */
  headerActions?: React.ReactNode;
  children?: React.ReactNode;
  /** Pie libre: reemplaza las acciones estándar. Preferí `primaryAction` / `secondaryAction`. */
  footer?: React.ReactNode;
  /** Acción principal: botón primario, a la derecha de todo. */
  primaryAction?: GuideDrawerAction;
  /**
   * Acción secundaria (a la izquierda de la principal). Por defecto: "Cancelar" (o "Cerrar" si no hay
   * acción principal) que llama a `onDismiss`. Pasá un objeto parcial para cambiar etiqueta / estado,
   * o `false` para ocultarla.
   */
  secondaryAction?: Partial<GuideDrawerAction> | false;
  /** Otras acciones neutras, entre la secundaria y la principal. */
  extraActions?: GuideDrawerAction[];
  /** Acción destructiva (Eliminar, Dar de baja): sola a la izquierda del pie, en rojo. */
  destructiveAction?: GuideDrawerAction;
  /** Bloquea el cierre (Esc, backdrop, X y Cancelar por defecto), p. ej. mientras se guarda o sube. */
  preventDismiss?: boolean;
  /** Si es true, cerrar pide confirmación ("¿Descartar los cambios?"). */
  confirmDiscard?: boolean;
  /** Etiqueta accesible del panel; si falta, el panel se rotula con el título. */
  ariaLabel?: string;
  /** Etiqueta del botón X (por defecto "Cerrar"). */
  closeLabel?: string;
  modalType?: OverlayDrawerProps['modalType'];
  className?: string;
  bodyClassName?: string;
  testId?: string;
}

const useGuideDrawerStyles = makeStyles({
  surface: {
    maxWidth: '100vw',
    backgroundColor: 'var(--ichtys-surface)',
    color: 'var(--ichtys-text)',
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    boxShadow: 'var(--ichtys-drawer-shadow)',
    ...shorthands.borderLeft('var(--ichtys-drawer-border-width, 1px)', 'solid', 'var(--ichtys-border)'),
  },
  header: {
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL, tokens.spacingVerticalL),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', 'var(--ichtys-border)'),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalM,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flexGrow: 1,
  },
  eyebrow: {
    fontSize: '13px',
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: 'var(--ichtys-text-secondary)',
  },
  title: {
    ...shorthands.margin(0),
    fontSize: tokens.fontSizeBase600,
    lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightBold,
    color: 'var(--ichtys-text)',
    overflowWrap: 'anywhere',
  },
  titleSmall: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  body: {
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL),
  },
  // flexGrow lets a drawer that turns its body into a flex column (bodyClassName) fill the full height
  bodyStack: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  footer: {
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXXL),
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', 'var(--ichtys-border)'),
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
    width: '100%',
    flexWrap: 'wrap',
  },
  footerStart: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  footerEnd: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalS,
    marginLeft: 'auto',
  },
  danger: {
    color: 'var(--ichtys-error)',
    ...shorthands.borderColor('var(--ichtys-error)'),
    ':hover': {
      color: 'var(--ichtys-error)',
      ...shorthands.borderColor('var(--ichtys-error)'),
    },
    ':hover:active': {
      color: 'var(--ichtys-error)',
      ...shorthands.borderColor('var(--ichtys-error)'),
    },
  },
});

const renderAction = (
  action: GuideDrawerAction,
  appearance: 'primary' | 'secondary',
  role: 'primary' | 'secondary' | 'extra' | 'destructive',
  className?: string
) => {
  const busy = !!action.loading;
  return (
    <Button
      key={role}
      appearance={appearance}
      type={action.type ?? 'button'}
      form={action.form}
      className={className}
      icon={busy ? <Spinner size="extra-tiny" /> : action.icon}
      onClick={action.onClick}
      disabled={action.disabled || busy}
      aria-busy={busy || undefined}
      aria-label={action.ariaLabel}
      data-drawer-action={role}
      data-testid={action.testId}
    >
      {action.label}
    </Button>
  );
};

export const GuideDrawer = ({
  open,
  onDismiss,
  onOpenChange,
  size = 'medium',
  eyebrow,
  title,
  subtitle,
  headerActions,
  children,
  footer,
  primaryAction,
  secondaryAction,
  extraActions,
  destructiveAction,
  preventDismiss = false,
  confirmDiscard = false,
  ariaLabel,
  closeLabel,
  modalType = 'modal',
  className,
  bodyClassName,
  testId,
}: GuideDrawerProps) => {
  const { t } = useTranslation();
  const styles = useGuideDrawerStyles();
  const titleId = useId('guide-drawer-title');
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    if (!open) setConfirming(false);
  }, [open]);

  const requestDismiss = () => {
    if (preventDismiss) return;
    if (confirmDiscard) {
      setConfirming(true);
      return;
    }
    onDismiss();
  };

  const hasFooterActions = !!primaryAction || !!destructiveAction || (extraActions?.length ?? 0) > 0;
  const showFooter = footer !== undefined || hasFooterActions || (secondaryAction !== undefined && secondaryAction !== false);

  const secondary: GuideDrawerAction | undefined =
    secondaryAction === false
      ? undefined
      : {
          label: primaryAction ? t('guideDrawer.cancel') : t('guideDrawer.close'),
          onClick: requestDismiss,
          disabled: preventDismiss,
          ...secondaryAction,
        };

  const widthStyle =
    size === 'full' || size === 'wide'
      ? undefined
      : ({ '--fui-Drawer--size': `${GUIDE_DRAWER_WIDTHS[size]}px` } as React.CSSProperties);

  return (
    <OverlayDrawer
      open={open}
      position="end"
      size={size === 'full' ? 'full' : 'medium'}
      modalType={modalType}
      style={widthStyle}
      className={mergeClasses('ichtys-drawer', styles.surface, className)}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : titleId}
      data-size={size}
      data-testid={testId}
      onOpenChange={(event, data) => {
        onOpenChange?.(event, data);
        if (!data.open) requestDismiss();
      }}
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.headerText}>
            {eyebrow && (
              <div className={styles.eyebrow} data-guide-drawer="eyebrow">
                {eyebrow}
              </div>
            )}
            <h2 id={titleId} className={mergeClasses(styles.title, size === 'small' && styles.titleSmall)}>
              {title}
            </h2>
            {subtitle && (
              <div className={styles.eyebrow} data-guide-drawer="subtitle">
                {subtitle}
              </div>
            )}
          </div>
          <div className={styles.headerActions}>
            {headerActions}
            <Button
              appearance="subtle"
              aria-label={closeLabel ?? t('guideDrawer.close')}
              icon={<Dismiss24Regular />}
              onClick={requestDismiss}
              disabled={preventDismiss}
              data-drawer-action="close"
            />
          </div>
        </div>
      </DrawerHeader>
      <DrawerBody className={mergeClasses(styles.body, bodyClassName)}>
        <div className={styles.bodyStack}>{children}</div>
      </DrawerBody>
      {showFooter && (
        <DrawerFooter className={styles.footer}>
          {footer !== undefined ? (
            footer
          ) : (
            <div className={styles.footerRow}>
              {destructiveAction && (
                <div className={styles.footerStart}>
                  {renderAction(destructiveAction, 'secondary', 'destructive', styles.danger)}
                </div>
              )}
              <div className={styles.footerEnd}>
                {secondary && renderAction(secondary, 'secondary', 'secondary')}
                {extraActions?.map((action, index) => (
                  <React.Fragment key={`extra-${index}`}>{renderAction(action, 'secondary', 'extra')}</React.Fragment>
                ))}
                {primaryAction && renderAction(primaryAction, 'primary', 'primary')}
              </div>
            </div>
          )}
        </DrawerFooter>
      )}
      <Dialog open={confirming} modalType="alert" onOpenChange={(_, data) => !data.open && setConfirming(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('guideDrawer.discardTitle')}</DialogTitle>
            <DialogContent>{t('guideDrawer.discardMessage')}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirming(false)}>
                {t('guideDrawer.keepEditing')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirming(false);
                  onDismiss();
                }}
              >
                {t('guideDrawer.discardConfirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </OverlayDrawer>
  );
};

const useSectionStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
  },
  headText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  title: {
    ...shorthands.margin(0),
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightBold,
    color: 'var(--ichtys-text)',
  },
  description: {
    ...shorthands.margin(0),
    fontSize: '13px',
    lineHeight: tokens.lineHeightBase200,
    color: 'var(--ichtys-text-secondary)',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    columnGap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
});

export interface GuideDrawerSectionProps {
  /** Título de sección: 16 Bold. */
  title?: React.ReactNode;
  /** Descripción opcional: 13 Regular, texto secundario. */
  description?: React.ReactNode;
  /** Acciones a la derecha del título (botones sutiles). */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  testId?: string;
}

/** Sección del cuerpo de un GuideDrawer (título 16 Bold + descripción secundaria + contenido con ritmo de 16 px). */
export const GuideDrawerSection = ({ title, description, actions, children, className, testId }: GuideDrawerSectionProps) => {
  const styles = useSectionStyles();
  const headingId = useId('guide-drawer-section');
  return (
    <section className={mergeClasses(styles.root, className)} aria-labelledby={title ? headingId : undefined} data-testid={testId}>
      {(title || description || actions) && (
        <div className={styles.head}>
          <div className={styles.headText}>
            {title && (
              <h3 id={headingId} className={styles.title}>
                {title}
              </h3>
            )}
            {description && <p className={styles.description}>{description}</p>}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
};

const useFormStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
});

export interface GuideDrawerFormProps extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  /** Se llama al enviar (Enter en un campo o un botón `type="submit"` con `form={id}`); ya hace preventDefault. */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  testId?: string;
}

/** `<form>` del cuerpo de un GuideDrawer con el ritmo vertical de la guía (16 px) y `noValidate`. */
export const GuideDrawerForm = ({ onSubmit, className, children, testId, ...rest }: GuideDrawerFormProps) => {
  const styles = useFormStyles();
  return (
    <form
      noValidate
      {...rest}
      className={mergeClasses(styles.root, className)}
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit?.(event);
      }}
    >
      {children}
    </form>
  );
};

export default GuideDrawer;
